import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BadgeCheckIcon, XIcon } from 'lucide-react';
import type { Purchase } from '../types/purchase';
type PurchaseToastProps = {
  purchases: Purchase[];
};
const VISIBLE_MS = 6000;
const FIRST_DELAY_MS = 8000;
export function PurchaseToast({
  purchases
}: PurchaseToastProps) {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (purchases.length === 0 || dismissed) return;
    let showTimer: number;
    let hideTimer: number;
    let i = 0;
    const schedule = (delay: number) => {
      showTimer = window.setTimeout(() => {
        setIndex(i % purchases.length);
        setVisible(true);
        hideTimer = window.setTimeout(() => {
          setVisible(false);
          i += 1;
          schedule(randomBetween(40000, 60000));
        }, VISIBLE_MS);
      }, delay);
    };
    schedule(FIRST_DELAY_MS);
    return () => {
      window.clearTimeout(showTimer);
      window.clearTimeout(hideTimer);
    };
  }, [purchases, dismissed]);
  const purchase = purchases[index];
  if (!purchase) return null;
  return <div className="pointer-events-none fixed bottom-4 left-4 right-4 z-50 sm:right-auto" aria-live="polite" data-id="element-56" data-mp="237z3w5jb6xol">
      <AnimatePresence data-id="element-57" data-mp="2846be2rcsh8n">
        {visible && !dismissed && <motion.div initial={{
        opacity: 0,
        y: 16,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 12
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} className="pointer-events-auto flex w-full items-center gap-3 rounded-2xl border border-beige-200 bg-paper p-3 pr-9 shadow-[0_20px_40px_-20px_rgba(17,43,42,0.55)] sm:w-[340px]" data-id="element-58" data-mp="1bh1bz3ornh46">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-petrol-800 text-gold-300" data-id="element-59" data-mp="rq6e6zjw75p3">
              <BadgeCheckIcon className="h-5 w-5" aria-hidden="true" data-id="element-60" data-mp="201rddbin18au" />
            </span>
            <div className="min-w-0" data-id="element-61" data-mp="1v2y4jweaq5h0">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-sage-700" data-id="element-62" data-mp="1v1v8la249cu8">Compra verificada</p>
              <p className="truncate text-sm text-petrol-900" data-id="element-63" data-mp="28ttelc0j6sm6">
                <strong className="font-semibold" data-id="element-64" data-mp="24s7wnc953o54">{purchase.name}</strong> de {purchase.city}, {purchase.province}
              </p>
              <p className="text-xs text-ink/60" data-id="element-65" data-mp="1h3oxpnbzcnpp">
                compró el Kit Sexualidad Práctica
                {purchase.purchasedAt && ` · ${timeAgo(purchase.purchasedAt)}`}
              </p>
            </div>
            <button type="button" onClick={() => setDismissed(true)} aria-label="Cerrar aviso" className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full text-ink/40 transition-colors duration-150 ease-out hover:bg-beige-100 hover:text-ink/70" data-id="element-66" data-mp="1q54xl15657fo">
              <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-67" data-mp="350elz1ydx7jr" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function randomBetween(min: number, max: number) {
  return Math.floor(min + Math.random() * (max - min));
}
function timeAgo(iso: string) {
  const mins = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `hace ${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.round(hours / 24);
  return `hace ${days} día${days > 1 ? 's' : ''}`;
}